import React, { createContext, useContext, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { gaEvent, gaPageView } from '@/lib/analytics';
import { useAnalyticsConsent } from '@/hooks/useAnalyticsConsent';

interface AnalyticsEvent {
  event_type: string;
  event_name: string;
  event_data?: Record<string, any>;
  page_path?: string;
  page_title?: string;
}

interface AnalyticsContextType {
  trackEvent: (event: AnalyticsEvent) => Promise<void>;
  trackPageView: (path: string, title?: string) => Promise<void>;
  trackClick: (element: string, data?: Record<string, any>) => Promise<void>;
  trackFeatureUsage: (feature: string, action: string, data?: Record<string, any>) => Promise<void>;
  sessionId: string | null;
  isTrackingEnabled: boolean;
  enableTracking: () => void;
  disableTracking: () => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Database-side event tracking stays disabled (write amplification).
  // Events are forwarded to Google Analytics, which itself is consent-gated.
  const { hasAnalyticsConsent } = useAnalyticsConsent();
  const location = useLocation();

  // SPA page views: gtag only auto-tracks the initial load
  useEffect(() => {
    if (!hasAnalyticsConsent) return;
    gaPageView(location.pathname + location.search);
  }, [hasAnalyticsConsent, location.pathname, location.search]);

  const trackEvent = useCallback(async (event: AnalyticsEvent) => {
    gaEvent(event.event_name, {
      event_category: event.event_type,
      page_path: event.page_path,
      ...(event.event_data ?? {}),
    });
  }, []);

  const trackPageView = useCallback(async (path: string, title?: string) => {
    gaPageView(path, title);
  }, []);

  const trackClick = useCallback(async (element: string, data?: Record<string, any>) => {
    gaEvent('click', { element, ...(data ?? {}) });
  }, []);

  const trackFeatureUsage = useCallback(
    async (feature: string, action: string, data?: Record<string, any>) => {
      gaEvent('feature_usage', { feature, action, ...(data ?? {}) });
    },
    [],
  );

  const enableTracking = useCallback(() => {}, []);
  const disableTracking = useCallback(() => {}, []);

  const value: AnalyticsContextType = {
    trackEvent,
    trackPageView,
    trackClick,
    trackFeatureUsage,
    sessionId: null,
    isTrackingEnabled: hasAnalyticsConsent,
    enableTracking,
    disableTracking,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
